/**
 * API Client
 * Axios instance with authentication and error handling
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_URL } from '../services/api';
import { getAuthToken, clearAuthData } from '../utils/storage.utils';
import { ApiError, ApiErrorDetail } from '../types';

/**
 * Create configured Axios instance
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 seconds for video uploads
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Add auth token to requests
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle errors globally
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<ApiErrorDetail>) => {
    // Handle different error types
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      // 401 Unauthorized - Clear auth data and redirect to login
      if (status === 401) {
        clearAuthData();
        window.location.href = '/login';
      }

      // Format error message
      let errorMessage = 'An error occurred';

      if (data?.detail) {
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          // FastAPI validation errors
          errorMessage = data.detail.map((err) => err.msg).join(', ');
        }
      }

      const apiError: ApiError = {
        message: errorMessage,
        status,
        detail: typeof data?.detail === 'string' ? data.detail : undefined,
      };

      return Promise.reject(apiError);
    } else if (error.request) {
      // Network error
      const apiError: ApiError = {
        message: 'Network error. Please check your connection.',
        status: 0,
      };
      return Promise.reject(apiError);
    } else {
      // Other errors
      const apiError: ApiError = {
        message: error.message || 'An unexpected error occurred',
        status: 0,
      };
      return Promise.reject(apiError);
    }
  }
);

export default apiClient;